import {
  BoxGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  type Camera,
} from 'three';
import { ITEM_CATALOG, ItemId, type ItemDef } from '../items/catalog';

type WeaponKind = 'pickaxe' | 'sword' | 'mace' | 'blaster';

function kindFor(id: ItemId | null): WeaponKind {
  if (!id) return 'pickaxe';
  if (id.startsWith('pickaxe_')) return 'pickaxe';
  if (id.startsWith('sword_')) return 'sword';
  if (id === ItemId.MaceIron) return 'mace';
  if (id === ItemId.BlasterPlasma) return 'blaster';
  return 'pickaxe';
}

/**
 * First-person held gear parented to the camera.
 * Swaps mesh layout by item kind; colors follow the item catalog.
 */
export class HeldToolView {
  readonly root = new Group();

  private readonly handleMat: MeshStandardMaterial;
  private readonly gripMat: MeshStandardMaterial;
  private readonly headMat: MeshStandardMaterial;
  private readonly accentMat: MeshStandardMaterial;

  private readonly pickaxeGroup: Group;
  private readonly swordGroup: Group;
  private readonly maceGroup: Group;
  private readonly blasterGroup: Group;

  private itemId: ItemId | null = ItemId.PickaxeRusty;
  private swingT = 0;
  private swinging = false;
  private bobPhase = 0;
  private readonly rest = { x: 0.28, y: -0.28, z: -0.45 };

  constructor(camera: Camera) {
    this.handleMat = new MeshStandardMaterial({
      color: 0x6e4524,
      roughness: 0.92,
      metalness: 0.04,
    });
    this.gripMat = new MeshStandardMaterial({
      color: 0x3a2414,
      roughness: 0.95,
      metalness: 0.02,
    });
    this.headMat = new MeshStandardMaterial({
      color: 0x8a5a3a,
      roughness: 0.38,
      metalness: 0.72,
      emissive: new Color(0x2a1508),
      emissiveIntensity: 0.35,
    });
    this.accentMat = new MeshStandardMaterial({
      color: 0x5a3a22,
      roughness: 0.32,
      metalness: 0.78,
      emissive: new Color(0x000000),
      emissiveIntensity: 0.2,
    });

    this.pickaxeGroup = this.buildPickaxe();
    this.swordGroup = this.buildSword();
    this.maceGroup = this.buildMace();
    this.blasterGroup = this.buildBlaster();

    this.root.add(this.pickaxeGroup, this.swordGroup, this.maceGroup, this.blasterGroup);
    this.setVisibleGroup(this.pickaxeGroup);

    this.root.position.set(this.rest.x, this.rest.y, this.rest.z);
    camera.add(this.root);
    this.setItem(ItemId.PickaxeRusty);
  }

  get displayName(): string {
    if (!this.itemId) return 'Empty Hand';
    return ITEM_CATALOG[this.itemId]?.name ?? 'Unknown';
  }

  get currentItemId(): ItemId | null {
    return this.itemId;
  }

  setItem(id: ItemId | null): void {
    this.itemId = id;
    const kind = kindFor(id);
    const group =
      kind === 'sword'
        ? this.swordGroup
        : kind === 'mace'
          ? this.maceGroup
          : kind === 'blaster'
            ? this.blasterGroup
            : this.pickaxeGroup;
    this.setVisibleGroup(group);

    const def: ItemDef | null = id ? ITEM_CATALOG[id] : null;
    const hex = def ? parseInt(def.color.slice(1), 16) : 0x8a5a3a;
    this.headMat.color.setHex(hex);
    this.accentMat.color.setHex(hex);
    if (kind === 'blaster' || (def?.pickaxeTier !== undefined && def.pickaxeTier >= 3)) {
      this.headMat.emissive.setHex(hex);
      this.headMat.emissiveIntensity = 0.45;
      this.accentMat.emissive.setHex(hex);
      this.accentMat.emissiveIntensity = 0.35;
    } else {
      this.headMat.emissive.setHex(0x1a1008);
      this.headMat.emissiveIntensity = 0.25;
      this.accentMat.emissive.setHex(0x000000);
      this.accentMat.emissiveIntensity = 0;
    }
  }

  playSwing(): void {
    this.swinging = true;
    this.swingT = 0;
  }

  update(dt: number, moving: number): void {
    this.bobPhase += dt * (6 + moving * 8);

    // Chop = pitch around local X (back → front). Avoid Z-roll, which reads as a sideways sweep.
    let pitch = 0;
    let thrust = 0;
    let lift = 0;
    if (this.swinging) {
      const kind = kindFor(this.itemId);
      const speed = kind === 'blaster' ? 14 : 10;
      this.swingT += dt * speed;
      if (this.swingT >= Math.PI) {
        this.swinging = false;
        this.swingT = 0;
      } else {
        const s = Math.sin(this.swingT);
        if (kind === 'blaster') {
          // Recoil: kick back along view axis.
          thrust = s * 0.1;
          pitch = s * 0.12;
        } else {
          // Raise on the first half, slam forward/down on the second.
          // s goes 0→1→0 over the swing; use cos for wind-up vs follow-through.
          const windUp = Math.cos(this.swingT); // 1 → 0 → -1
          pitch = -windUp * 0.85; // tip back, then tip forward
          thrust = s * 0.18; // push toward the crosshair mid-swing
          lift = windUp * 0.06; // lift on wind-up, drop on strike
        }
      }
    }

    const bobY = Math.sin(this.bobPhase) * 0.012 * (0.35 + moving);
    const bobX = Math.cos(this.bobPhase * 0.5) * 0.006 * moving;

    this.root.position.set(
      this.rest.x + bobX,
      this.rest.y + bobY + lift,
      this.rest.z - thrust,
    );
    // Rest pose: slight tip + roll. Swing only adds pitch (X), not roll (Z).
    this.root.rotation.set(0.15 + pitch, 0.12, -0.28);
  }

  dispose(): void {
    this.root.removeFromParent();
    this.root.traverse((obj) => {
      const mesh = obj as Mesh;
      if (mesh.isMesh) {
        mesh.geometry.dispose();
      }
    });
    this.handleMat.dispose();
    this.gripMat.dispose();
    this.headMat.dispose();
    this.accentMat.dispose();
  }

  private setVisibleGroup(group: Group): void {
    this.pickaxeGroup.visible = group === this.pickaxeGroup;
    this.swordGroup.visible = group === this.swordGroup;
    this.maceGroup.visible = group === this.maceGroup;
    this.blasterGroup.visible = group === this.blasterGroup;
  }

  private buildPickaxe(): Group {
    const g = new Group();

    // Tapered wooden shaft
    const shaft = new Mesh(new CylinderGeometry(0.015, 0.024, 0.56, 10), this.handleMat);
    shaft.position.set(0, -0.08, 0);

    // Dark leather wrap near the grip
    const grip = new Mesh(new CylinderGeometry(0.026, 0.028, 0.11, 10), this.gripMat);
    grip.position.set(0, -0.22, 0);
    const gripBand = new Mesh(new CylinderGeometry(0.029, 0.029, 0.018, 10), this.accentMat);
    gripBand.position.set(0, -0.165, 0);

    // Butt cap
    const pommel = new Mesh(new CylinderGeometry(0.03, 0.02, 0.035, 8), this.accentMat);
    pommel.position.set(0, -0.36, 0);

    // Metal ferrule where the head seats
    const ferrule = new Mesh(new CylinderGeometry(0.032, 0.026, 0.055, 10), this.accentMat);
    ferrule.position.set(0, 0.175, 0);

    // Forged eye / socket around the shaft top
    const eye = new Mesh(new BoxGeometry(0.055, 0.065, 0.065), this.headMat);
    eye.position.set(0, 0.235, 0);

    // Crossbar body of the head
    const bar = new Mesh(new BoxGeometry(0.2, 0.048, 0.048), this.headMat);
    bar.position.set(0, 0.235, 0);

    // Slightly thicker shoulders before the points
    const shoulderL = new Mesh(new BoxGeometry(0.05, 0.058, 0.052), this.headMat);
    shoulderL.position.set(0.1, 0.228, 0);
    shoulderL.rotation.z = 0.18;
    const shoulderR = new Mesh(new BoxGeometry(0.05, 0.058, 0.052), this.headMat);
    shoulderR.position.set(-0.1, 0.228, 0);
    shoulderR.rotation.z = -0.18;

    // Pointed pick tips (cone apex along +Y by default)
    const tipL = new Mesh(new ConeGeometry(0.032, 0.13, 7), this.accentMat);
    tipL.position.set(0.195, 0.21, 0);
    tipL.rotation.z = -Math.PI / 2 - 0.28;
    const tipR = new Mesh(new ConeGeometry(0.032, 0.13, 7), this.accentMat);
    tipR.position.set(-0.195, 0.21, 0);
    tipR.rotation.z = Math.PI / 2 + 0.28;

    // Small top spike for silhouette
    const spike = new Mesh(new ConeGeometry(0.018, 0.07, 6), this.headMat);
    spike.position.set(0, 0.305, 0);

    g.add(shaft, grip, gripBand, pommel, ferrule, eye, bar, shoulderL, shoulderR, tipL, tipR, spike);
    // Tips along view axis (forward/back) so a pitch chop drives a point into the block.
    g.rotation.y = Math.PI / 2;
    return g;
  }

  private buildSword(): Group {
    const g = new Group();

    // Leather-wrapped grip
    const grip = new Mesh(new CylinderGeometry(0.016, 0.018, 0.14, 10), this.gripMat);
    grip.position.set(0, -0.14, 0);
    const wrap = new Mesh(new CylinderGeometry(0.019, 0.02, 0.04, 10), this.handleMat);
    wrap.position.set(0, -0.1, 0);

    // Pommel
    const pommel = new Mesh(new CylinderGeometry(0.028, 0.02, 0.04, 8), this.accentMat);
    pommel.position.set(0, -0.23, 0);
    const pommelCap = new Mesh(new ConeGeometry(0.022, 0.03, 6), this.headMat);
    pommelCap.position.set(0, -0.26, 0);
    pommelCap.rotation.x = Math.PI;

    // Crossguard
    const guard = new Mesh(new BoxGeometry(0.18, 0.028, 0.04), this.accentMat);
    guard.position.set(0, -0.055, 0);
    const guardCurveL = new Mesh(new BoxGeometry(0.04, 0.022, 0.032), this.accentMat);
    guardCurveL.position.set(0.095, -0.042, 0);
    guardCurveL.rotation.z = -0.45;
    const guardCurveR = new Mesh(new BoxGeometry(0.04, 0.022, 0.032), this.accentMat);
    guardCurveR.position.set(-0.095, -0.042, 0);
    guardCurveR.rotation.z = 0.45;
    const ricasso = new Mesh(new BoxGeometry(0.038, 0.04, 0.022), this.headMat);
    ricasso.position.set(0, -0.03, 0);

    // Blade body — wide near guard, thinner toward tip
    const forte = new Mesh(new BoxGeometry(0.055, 0.2, 0.016), this.headMat);
    forte.position.set(0, 0.085, 0);
    const middle = new Mesh(new BoxGeometry(0.045, 0.18, 0.014), this.headMat);
    middle.position.set(0, 0.26, 0);
    const foible = new Mesh(new BoxGeometry(0.032, 0.12, 0.012), this.headMat);
    foible.position.set(0, 0.39, 0);

    // Shallow fuller (darker groove down the center)
    const fuller = new Mesh(new BoxGeometry(0.012, 0.38, 0.004), this.accentMat);
    fuller.position.set(0, 0.2, 0.007);

    // Pointed tip
    const tip = new Mesh(new ConeGeometry(0.02, 0.09, 4), this.headMat);
    tip.position.set(0, 0.485, 0);
    tip.rotation.y = Math.PI / 4; // diamond cross-section

    g.add(
      grip,
      wrap,
      pommel,
      pommelCap,
      guard,
      guardCurveL,
      guardCurveR,
      ricasso,
      forte,
      middle,
      foible,
      fuller,
      tip,
    );
    // Edge in the swing plane (same fix as the pickaxe head).
    g.rotation.y = Math.PI / 2;
    return g;
  }

  private buildMace(): Group {
    const g = new Group();
    const handle = new Mesh(new BoxGeometry(0.05, 0.42, 0.05), this.handleMat);
    handle.position.set(0, -0.02, 0);
    const head = new Mesh(new BoxGeometry(0.16, 0.16, 0.16), this.headMat);
    head.position.set(0, 0.24, 0);
    const spike = new Mesh(new BoxGeometry(0.04, 0.1, 0.04), this.accentMat);
    spike.position.set(0, 0.34, 0);
    g.add(handle, head, spike);
    return g;
  }

  private buildBlaster(): Group {
    const g = new Group();
    const body = new Mesh(new BoxGeometry(0.08, 0.1, 0.28), this.headMat);
    body.position.set(0, 0, -0.02);
    const barrel = new Mesh(new CylinderGeometry(0.025, 0.03, 0.22, 8), this.accentMat);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 0.01, -0.22);
    const grip = new Mesh(new BoxGeometry(0.05, 0.14, 0.08), this.handleMat);
    grip.position.set(0, -0.1, 0.04);
    g.add(body, barrel, grip);
    return g;
  }
}
